import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useGapAnalysisExcelQuery, useTrainingMatrixExcelQuery } from '../API/rtk-query-api/store/tmsApi'
import { useAppSelector } from '../redux/hooks'
import { resolveExportFormatValue } from '../utils/export-format-util'

export const useExportExcel = () => {
  const trainingMatrixState = useAppSelector(state => state.trainingMatrix)
  const trainingMatrixFiltersState = useAppSelector(state => state.trainingMatrixFilters)
  const gapAnalysisFiltersState = useAppSelector(state => state.gapAnalysisFilters)
  const gapAnalysisState = useAppSelector(state => state.gapAnalysis)

  const [fetchTrainingMatrixExcel, setFetchTrainingMatrixExcel] = useState(false)
  const [fetchGapAnalysisExcel, setFetchGapAnalysisExcel] = useState(false)

  const style = resolveExportFormatValue(
    trainingMatrixFiltersState.showDates,
    trainingMatrixFiltersState.selectedDisplayDate
  )

  const {
    data: trainingMatrixExcel,
    isFetching: isFetchingTrainingMatrixExcel,
    isError: isTrainingMatrixExcelError
  } = useTrainingMatrixExcelQuery(
    {
      reportHash: trainingMatrixState.hashKey ?? '',
      style: style,
      showGroups: trainingMatrixFiltersState.showGroups,
      showManagers: trainingMatrixFiltersState.showManagersColumn,
      hierarchies: trainingMatrixFiltersState.columnsToShow.map(h => h.value),
      showOverallPercentageOfCompliance: trainingMatrixFiltersState.showOverallCompliancePercentage,
      showPercentageOfComplianceForIndividuals: trainingMatrixFiltersState.showPercentageOfComplianceForIndividuals,
      showPercentageOfComplianceForCompetencies: trainingMatrixFiltersState.showPercentageOfComplianceForCompetencies,
      showPercentageOfComplianceForCompetencyGroups: trainingMatrixFiltersState.showPercentageOfComplianceForGroups,
      useColorBlindPalette: trainingMatrixFiltersState.isColourBlindThemeEnabled
    },
    {
      skip: !trainingMatrixState.hashKey || !fetchTrainingMatrixExcel
    }
  )

  const {
    data: gapAnalysisExcel,
    isFetching: isFetchingGapAnalysisExcel,
    isError: isGapAnalysisExcelError
  } = useGapAnalysisExcelQuery(
    {
      reportHash: gapAnalysisState.hashKey ?? '',
      visibleColumns: gapAnalysisFiltersState.selectedColumns.map(column => column.value)
    },
    {
      skip: !gapAnalysisState.hashKey || !fetchGapAnalysisExcel
    }
  )

  const handleFetchExport = (selectedView: number) => {
    //Training matrix = 1; Gap Analysis = 2
    if (selectedView === 1) {
      setFetchTrainingMatrixExcel(true)
    } else {
      setFetchGapAnalysisExcel(true)
    }
  }

  const downloadExcel = (filePath: string, fileName: string) => {
    const link = document.createElement('a')
    link.href = filePath
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  useEffect(() => {
    if (trainingMatrixExcel && !isFetchingTrainingMatrixExcel && !isTrainingMatrixExcelError) {
      downloadExcel(String(trainingMatrixExcel), 'Training Matrix')
      setFetchTrainingMatrixExcel(false)
    } else if (!isFetchingTrainingMatrixExcel && isTrainingMatrixExcelError) {
      toast.error('Error getting excel file')
      setFetchTrainingMatrixExcel(false)
    }
  }, [trainingMatrixExcel, isFetchingTrainingMatrixExcel, isTrainingMatrixExcelError])

  useEffect(() => {
    if (gapAnalysisExcel && !isFetchingGapAnalysisExcel && !isGapAnalysisExcelError) {
      downloadExcel(String(gapAnalysisExcel), 'Gap Analysis')
      setFetchGapAnalysisExcel(false)
    } else if (!isFetchingGapAnalysisExcel && isGapAnalysisExcelError) {
      toast.error('Error getting excel file')
      setFetchGapAnalysisExcel(false)
    }
  }, [gapAnalysisExcel, isFetchingGapAnalysisExcel, isGapAnalysisExcelError])

  return {
    handleFetchExport
  }
}
